'use client';

import { useSearchParams } from 'next/navigation';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import defaultFlags from '@/consts/enabled-features.json';

type FeatureFlags = Record<string, boolean>;

const COOKIE_KEY = 'cmong-feature-overrides';

const FeatureFlagContext = createContext<FeatureFlags>(defaultFlags);

function getCookieOverrides(): FeatureFlags {
  if (typeof document === 'undefined') return {};
  try {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${COOKIE_KEY}=`));
    if (!match) return {};
    return JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
  } catch {
    return {};
  }
}

function setCookieOverrides(overrides: FeatureFlags) {
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(JSON.stringify(overrides))}; path=/; max-age=${60 * 60 * 24 * 365}`;
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const searchParams = useSearchParams();

  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const overrides = getCookieOverrides();
    return { ...defaultFlags, ...overrides };
  });

  useEffect(() => {
    const ffOn = searchParams.get('ff');
    const ffOff = searchParams.get('ff-off');
    if (!ffOn && !ffOff) return;

    const overrides = getCookieOverrides();

    if (ffOn) {
      ffOn.split(',').forEach((key) => {
        overrides[key.trim()] = true;
      });
    }

    if (ffOff) {
      ffOff.split(',').forEach((key) => {
        delete overrides[key.trim()];
      });
    }

    setCookieOverrides(overrides);
    setFlags({ ...defaultFlags, ...overrides });
  }, [searchParams]);

  return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
}

export function useFlag(name: string): boolean {
  const flags = useContext(FeatureFlagContext);
  return !!flags[name];
}
